import { syncUiType } from './syncUiTypes'
import { ReduxAction, State } from '../../../reducers/reducerType'
import { mediaType as MediaType } from '@bt-react/constants'
import * as actions from '../..'
import roomActions from '../../room'
import setupActions from '../../setup'
import { setLiveTime, setIsGoingLive, unsetIsGoingLive, setToLiveEndedConfirmed,
  setIsEndingLive, unsetIsEndingLive, extendGigTerminationSuccess } from '../../gig/gigActions'
import isEqual from 'lodash/isEqual'
import has from 'lodash/has'
import slidesActions from '../../slides'
import trackActions from '../../tracks'
import { TwilioUIDocValueInterface } from '../../../reducers/docs/types'
import { LayoutsSlice } from '../../../slices/layouts'
import { LayoutType } from '../../../slices/layouts/layoutsSlice'
import { SyncUiQuestionsState } from '../../../reducers/docs/syncUi/types'

export const setEndingLive = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_ENDING_LIVE, payload: value })
export const setFocusOn = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_FOCUS_ON, payload: value })
export const setScreenshareFocus = (value: string | null): ReduxAction => ({ type: syncUiType.SET_SYNC_SCREENSHARE_FOCUS, payload: value })
export const setDominantSpeaker = (value): ReduxAction => ({type: syncUiType.SET_SYNC_DOMINANT_SPEAKER, payload: value })
export const setGigTerminationTime = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_GIG_TERMINATION_TIME, payload: value })
export const setGoingLive = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_GOING_LIVE, payload: value })
export const setLiveDateTime = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_LIVE_DATE_TIME, payload: value })
export const setLiveEnded = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_LIVE_ENDED, payload: value })
export const setMediaType = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_MEDIA_TYPE, payload: value })
export const setMute = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_MUTE, payload: value })
export const setPlayInMute = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_PLAYIN_MUTE, payload: value })
export const setParticipantInformation = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_PARTICIPANT_INFORMATION, payload: value })
export const setPresenterCount = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_PRESENTER_COUNT, payload: value })
export const setVideoDisplay = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_VIDEO_DISPLAY, payload: value })
export const setSlidePosition = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_SLIDE_POSITION, payload: value })
export const setPendingSlidesId = (value): ReduxAction => ({ type: syncUiType.SET_SYNC_PENDING_SLIDES_ID, payload: value })
export const setAutoSpotlight = (autoSpotlight: boolean): ReduxAction => ({ type: syncUiType.SET_SYNC_AUTO_SPOTLIGHT, payload: autoSpotlight })
export const setEventStreamUrl = (url: string): ReduxAction => ({ type: syncUiType.SET_SYNC_EVENT_STREAM_URL, payload: url })
export const setLayouts = (value: LayoutsSlice): ReduxAction => ({ type: syncUiType.SET_SYNC_LAYOUTS, payload: value })
export const setQuestions = (questions: SyncUiQuestionsState): ReduxAction => ({ type: syncUiType.SET_SYNC_QUESTIONS, payload: questions })
export const noSyncUiUpdatesToApply = (): ReduxAction => ({ type: syncUiType.NO_SYNC_UI_UPDATES_TO_APPLY })
export const checkDialInNotification = (): ReduxAction => ({ type: syncUiType.SET_SYNC_CHECK_DIAL_IN_NOTIFICATION })
export const setUIState = (docValues: TwilioUIDocValueInterface, initialState = false) =>
  (dispatch, getState: () => State) => {

  if (!docValues && initialState) return dispatch({ type: syncUiType.SET_UI_INITIAL_STATE_ERROR })

  const {
    goingLive,
    endingLive,
    liveEnded,
    gigTerminationTime,
    liveDateTime,
    mute = [],
    playInMute = [],
    videoDisplay = [],
    presenterCount = 0,
    focusOn,
    screenShareFocus,
    mediaType = MediaType.WEBCAM,
    participantInformation,
    slidePosition = {
      selectedSlideStep: 0,
      selectedSlide: 0
    },
    pendingSlidesId = null,
    autoSpotlight,
    dominantSpeaker,
    eventStreamUrl,
    layouts,
    questions
  } = docValues

  const syncUi = getState().syncUi

  let updatesApplied = false

  if (typeof goingLive === 'boolean' && goingLive !== syncUi.goingLive) {

    updatesApplied = true

    dispatch(setGoingLive(goingLive))

    if (goingLive) dispatch(setIsGoingLive())
    else dispatch(unsetIsGoingLive())

  }

  if (typeof endingLive === 'boolean' && endingLive !== syncUi.endingLive) {

    updatesApplied = true

    dispatch(setEndingLive(endingLive))
    if (endingLive) dispatch(setIsEndingLive())
    else dispatch(unsetIsEndingLive())

  }

  if (typeof liveEnded === 'boolean' && liveEnded !== syncUi.liveEnded) {

    updatesApplied = true

    dispatch(setLiveEnded(liveEnded))
    dispatch(setToLiveEndedConfirmed())

  }

  /** Make sure that only a valid date is set */
  if (gigTerminationTime && gigTerminationTime !== syncUi.gigTerminationTime && Date.parse(gigTerminationTime)) {

    updatesApplied = true

    dispatch(setGigTerminationTime(gigTerminationTime))
    dispatch(extendGigTerminationSuccess(gigTerminationTime))

  }

  /** Make sure that only a valid date is set */
  if (liveDateTime && liveDateTime !== syncUi.liveDateTime && Date.parse(liveDateTime)) {

    updatesApplied = true

    dispatch(setLiveDateTime(liveDateTime))
    dispatch(setLiveTime(liveDateTime))

  }

  if (has(mute, 'length') && !isEqual(mute, syncUi.mute)) {

    updatesApplied = true

    const { BTID } = getState().user
    mute.forEach(id => {
      if (syncUi.mute.indexOf(id) === -1 && BTID === id) dispatch(trackActions.localParticipantMuted())
    })

    dispatch(setMute(mute))
    dispatch(trackActions.updateAudioMute(mute))

  }



  if (has(playInMute, 'length') && !isEqual(playInMute, syncUi.playInMute)) {

    updatesApplied = true
    dispatch(setPlayInMute(playInMute))

  }

  if (has(videoDisplay, 'length') && !isEqual(videoDisplay, syncUi.videoDisplay)) {

    updatesApplied = true

    dispatch(setVideoDisplay(videoDisplay))
    dispatch(roomActions.updateVideoDisplay(videoDisplay))

  }

  if ((focusOn || focusOn === null) && focusOn !== syncUi.focusOn) {

    updatesApplied = true

    dispatch(setFocusOn(focusOn))
    dispatch(roomActions.setFocusState(focusOn))

  }

  if ((screenShareFocus || screenShareFocus === null) && screenShareFocus !== syncUi.screenShareFocus) {

    updatesApplied = true

    dispatch(setScreenshareFocus(screenShareFocus))

  }


  if (dominantSpeaker && !isEqual(dominantSpeaker, syncUi.dominantSpeaker)) {

    updatesApplied = true

    dispatch(setDominantSpeaker(dominantSpeaker))

  }

  if (!isEqual(autoSpotlight, syncUi.autoSpotlight)) {

    updatesApplied = true

    dispatch(setAutoSpotlight(autoSpotlight))

  }

  if (!isEqual(eventStreamUrl, syncUi.eventStreamUrl)) {

    updatesApplied = true

    dispatch(setEventStreamUrl(eventStreamUrl))

  }

  if (layouts && !isEqual(layouts, syncUi.layouts)) {

    updatesApplied = true

    dispatch(setLayouts(layouts))

    if (layouts.mediaLayout.mediaLayoutType === LayoutType.Split
      && syncUi.mediaType !== MediaType.SPOTLIGHT) dispatch(checkDialInNotification())

  }

  if (questions && !isEqual(questions, syncUi.questions)) {

    updatesApplied = true

    dispatch(setQuestions(questions))

  }

  if (mediaType && mediaType !== syncUi.mediaType) {

    updatesApplied = true

    dispatch(setMediaType(mediaType))
    dispatch(roomActions.setMediaTypeState(mediaType))


    if (mediaType !== MediaType.WEBCAM &&
      syncUi.layouts.mediaLayout.mediaSidebarActive || mediaType === MediaType.SPOTLIGHT) dispatch(checkDialInNotification())

  }

  if (participantInformation && !isEqual(participantInformation, syncUi.participantInformation)) {

    updatesApplied = true

    dispatch(setParticipantInformation(participantInformation))
    dispatch(roomActions.setParticipantInformation(participantInformation))

  }

  if (presenterCount && presenterCount !== syncUi.presenterCount) {

    updatesApplied = true

    dispatch(setPresenterCount(presenterCount))
    dispatch(roomActions.setPresenterCount(presenterCount))

  }

  if (pendingSlidesId && pendingSlidesId !== syncUi.pendingSlidesId) {

    updatesApplied = true

    dispatch(setPendingSlidesId(pendingSlidesId))
    dispatch(slidesActions.setPendingSlidesId(pendingSlidesId))

  }

  if (slidePosition && !isEqual(slidePosition, syncUi.slidePosition)) {

    updatesApplied = true

    const newSlidePosition = { ...slidePosition }
    dispatch(setSlidePosition(newSlidePosition))
    dispatch(slidesActions.setSlidePosition(newSlidePosition))

  }


  if (initialState) dispatch(setupActions.setUIDocumentStateComplete())
  else {

    if (!updatesApplied) dispatch(noSyncUiUpdatesToApply())

  }

}

export default {
  setEndingLive,
  setFocusOn,
  setGigTerminationTime,
  setGoingLive,
  setLiveDateTime,
  setLiveEnded,
  setMediaType,
  setMute,
  setParticipantInformation,
  setPresenterCount,
  setVideoDisplay,
  setSlidePosition,
  setPendingSlidesId,
  setEventStreamUrl,
  setUIState
}
